import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { LocationStatus } from '@invenco/accounts-interface';
import type { CreateAsnDTO } from '@invenco/supply-interface';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { SelectItem } from '@/components/core/select';
import { SelectFormField } from '@/components/forms/select-form-field';
import { WizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useGateways } from '@/gateways/GatewayProvider';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { useIsFormValid } from '@/shared/hooks/use-is-form-valid';

const warehouseSchema = z.object({
  locationName: z.string().trim().min(1, 'Select a warehouse'),
});

export function WarehouseStep({
  step,
  onChange,
}: {
  step: WizardStepDefinition;
  onChange: (input: Partial<CreateAsnDTO>) => void;
}) {
  const { accountsGateway } = useGateways();
  const form = useForm({
    resolver: zodResolver(warehouseSchema),
    defaultValues: { locationName: '' },
  });
  const isValid = useIsFormValid(form, warehouseSchema);
  const { data: locations = [], isLoading } = useQueryWithInput({
    parentKey: 'locations',
    input: { status: LocationStatus.ACTIVE },
    query: (input, options) => accountsGateway.getLocations(input, options),
    select: ({ items }) => items,
  });

  const submitSection = async () => {
    let valid = false;

    await form.handleSubmit((data) => {
      onChange({ locationName: data.locationName });
      valid = true;
    })();

    return valid;
  };

  return (
    <WizardStep step={step} complete={isValid} onSubmitStep={submitSection}>
      <Card aria-label="To warehouse">
        <CardHeader>
          <CardTitle>To warehouse</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <SelectFormField
              control={form.control}
              name="locationName"
              aria-label="Warehouse"
              placeholder={isLoading ? 'Loading warehouses...' : 'Select'}
              className="w-full"
              disabled={isLoading}
            >
              {locations.map((location) =>
                location.name ? (
                  <SelectItem key={location.id ?? location.name} value={location.name}>
                    {location.name}
                  </SelectItem>
                ) : null,
              )}
            </SelectFormField>
          </Form>
        </CardContent>
      </Card>
    </WizardStep>
  );
}
